"use strict";
// Copyright © Veeam Software Group GmbH
Object.defineProperty(exports, "__esModule", { value: true });
exports.MovableManager = exports.getWindowSize = void 0;
const react_dom_1 = require("react-dom");
const reflect_1 = require("../helpers/reflect");
const getWindowSize = () => {
    return {
        width: Math.max(document.documentElement.clientWidth, window.innerWidth || 0),
        height: Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
    };
};
exports.getWindowSize = getWindowSize;
const getStyles = (x = 0, y = 0, z = 0) => {
    return { transform: `translate3d(${x}px, ${y}px, ${z}px)`, transition: 'none' };
};
const getBoundary = (targetRect, prevFixedOffset, windowSize) => {
    return {
        min: {
            x: -targetRect.left + prevFixedOffset.x,
            y: -targetRect.top + prevFixedOffset.y
        },
        max: {
            x: windowSize.width - targetRect.right + prevFixedOffset.x,
            y: windowSize.height - targetRect.bottom + prevFixedOffset.y
        }
    };
};
class MovableManager {
    constructor(onStyleChange, listener) {
        this.onStyleChange = onStyleChange;
        this.listener = listener;
        this.listenerRemovers = [];
        this.startPosition = null;
        this.prevOffset = { x: 0, y: 0 };
        this.prevFixedOffset = { x: 0, y: 0 };
        this.targetDomNode = null;
        this.targetRect = null;
        this.windowSize = (0, exports.getWindowSize)();
        this.boundary = null;
        this.prevPointerEventsStyle = '';
        this.onMouseMove = (e) => {
            if (this.targetDomNode) {
                const moveDelta = this.getMoveDelta({ x: e.clientX, y: e.clientY });
                this.startPosition = { x: e.pageX, y: e.pageY };
                const nextOffsetX = -moveDelta.x + this.prevOffset.x;
                const nextOffsetY = -moveDelta.y + this.prevOffset.y;
                let nextFixedOffsetX = nextOffsetX;
                let nextFixedOffsetY = nextOffsetY;
                if (nextOffsetX < 0 && this.boundary) {
                    nextFixedOffsetX = Math.max(nextOffsetX, this.boundary.min.x);
                }
                if (nextOffsetY < 0 && this.boundary) {
                    nextFixedOffsetY = Math.max(nextOffsetY, this.boundary.min.y);
                }
                if (nextOffsetX > 0 && this.boundary) {
                    nextFixedOffsetX = Math.min(nextOffsetX, this.boundary.max.x);
                }
                if (nextOffsetY > 0 && this.boundary) {
                    nextFixedOffsetY = Math.min(nextOffsetY, this.boundary.max.y);
                }
                const style = getStyles(Math.round(nextFixedOffsetX), Math.round(nextFixedOffsetY));
                this.prevOffset = {
                    x: nextOffsetX,
                    y: nextOffsetY
                };
                this.prevFixedOffset = {
                    x: nextFixedOffsetX,
                    y: nextFixedOffsetY
                };
                this.onStyleChange(style);
                requestAnimationFrame(() => {
                    if (this.targetDomNode) {
                        Object.assign(this.targetDomNode.style, style);
                    }
                });
            }
        };
        this.removeListeners = () => {
            this.listenerRemovers.forEach((cb) => cb());
        };
        this.addEvenListener = (event, listener, options = {}) => {
            document.addEventListener(event, listener, options);
            this.listenerRemovers.push(() => {
                document.removeEventListener(event, listener, options);
            });
        };
        this.getMoveDelta = (nextPosition) => {
            if (!this.startPosition) {
                return { x: 0, y: 0 };
            }
            if (!this.targetRect) {
                return { x: 0, y: 0 };
            }
            return {
                x: this.startPosition.x - nextPosition.x,
                y: this.startPosition.y - nextPosition.y
            };
        };
        this.onResize = () => {
            if (!this.targetDomNode) {
                return;
            }
            const boundingClientRect = this.targetDomNode.getBoundingClientRect();
            this.targetRect = {
                width: boundingClientRect.width,
                height: boundingClientRect.height,
                left: boundingClientRect.left,
                right: boundingClientRect.right,
                top: boundingClientRect.top,
                bottom: boundingClientRect.bottom
            };
            this.windowSize = (0, exports.getWindowSize)();
            this.boundary = getBoundary(this.targetRect, this.prevFixedOffset, this.windowSize);
            let nextFixedOffsetX = this.prevFixedOffset.x;
            let nextFixedOffsetY = this.prevFixedOffset.y;
            const isOutsideRight = this.windowSize.width - this.targetRect.width - this.targetRect.left < 0;
            const isOutsideLeft = this.targetRect.right < this.targetRect.width;
            const isOutsideBottom = this.windowSize.height - this.targetRect.height - this.targetRect.top < 0;
            const isOutsideTop = this.targetRect.bottom < this.targetRect.height;
            if (isOutsideRight) {
                nextFixedOffsetX = this.boundary.max.x;
            }
            if (isOutsideLeft) {
                nextFixedOffsetX = this.boundary.min.x;
            }
            if (isOutsideBottom) {
                nextFixedOffsetY = this.boundary.max.y;
            }
            if (isOutsideTop) {
                nextFixedOffsetY = this.boundary.min.y;
            }
            if (isOutsideRight && isOutsideLeft) {
                nextFixedOffsetX = 0;
            }
            if (isOutsideBottom && isOutsideTop) {
                nextFixedOffsetY = 0;
            }
            const style = getStyles(Math.round(nextFixedOffsetX), Math.round(nextFixedOffsetY));
            this.prevOffset = {
                x: nextFixedOffsetX,
                y: nextFixedOffsetY
            };
            this.prevFixedOffset = {
                x: nextFixedOffsetX,
                y: nextFixedOffsetY
            };
            this.onStyleChange(style);
            requestAnimationFrame(() => {
                if (this.targetDomNode) {
                    Object.assign(this.targetDomNode.style, style);
                }
            });
        };
        this.start = (position, target) => {
            if (typeof window !== 'undefined') {
                if (this.listener.onStart) {
                    this.listener.onStart();
                }
                this.startPosition = position;
                this.prevPointerEventsStyle = window.document.body.style.pointerEvents;
                this.targetDomNode = (0, reflect_1.isHTMLElement)(target)
                    ? target
                    : // eslint-disable-next-line react/no-find-dom-node
                        (0, react_dom_1.findDOMNode)((0, reflect_1.isComponent)(target) ? target : null);
                const boundingClientRect = this.targetDomNode
                    ? this.targetDomNode.getBoundingClientRect()
                    : null;
                this.targetRect = boundingClientRect
                    ? {
                        width: boundingClientRect.width,
                        height: boundingClientRect.height,
                        left: boundingClientRect.left,
                        right: boundingClientRect.right,
                        top: boundingClientRect.top,
                        bottom: boundingClientRect.bottom
                    }
                    : null;
                this.windowSize = (0, exports.getWindowSize)();
                if (this.targetRect) {
                    this.boundary = getBoundary(this.targetRect, this.prevFixedOffset, this.windowSize);
                }
                this.addEvenListener('mousemove', this.onMouseMove);
                this.addEvenListener('mouseup', this.stop, { capture: true });
                this.addEvenListener('blur', this.stop);
                this.addEvenListener('mousedown', this.stop);
            }
        };
        this.stop = () => {
            if (this.listener.onEnd) {
                this.listener.onEnd();
            }
            if (typeof window !== 'undefined') {
                Object.assign(window.document.body.style, {
                    pointerEvents: this.prevPointerEventsStyle
                });
                setTimeout(() => {
                    if (!window || !window.getSelection) {
                        return;
                    }
                    const selection = window.getSelection();
                    if (selection && typeof selection.removeAllRanges === 'function') {
                        selection.removeAllRanges();
                    }
                }, 0);
            }
            this.removeListeners();
            this.startPosition = null;
            this.prevOffset = this.prevFixedOffset;
        };
    }
}
exports.MovableManager = MovableManager;
//# sourceMappingURL=MovableManager.js.map